import { useState } from "react";

import { useFunctionDialogContext } from "@/components/FunctionsModal";
import { Button } from "@/components/ui/button";
import { Mcp } from "@/components/ui/icons";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { useMcpServers } from "@/queries/mcp-servers";
import { PromptLayerMcpTool } from "@/types";
import { isMcpTool } from "@/utils/utils";

const MCPToolDialog = () => {
  const {
    editingFunction,
    functions,
    setEditingFunction,
    setFunctions,
    setIsAddingFunction,
    setIsShowingMcpTool,
  } = useFunctionDialogContext();
  const workspaceId = useUser()?.activeWorkspaceId ?? 0;
  const userToken = useAuth()?.userToken ?? "";
  const [search, setSearch] = useState("");
  const servers = useMcpServers({ workspaceId, userToken, search });
  const editingMcp = editingFunction && isMcpTool(editingFunction) ? editingFunction : undefined;

  const handleClose = () => {
    setIsShowingMcpTool(false);
    setIsAddingFunction(false);
    setEditingFunction(null);
  };

  const handleSelect = (serverId: number, serverName: string) => {
    if (!setFunctions) {
      return;
    }
    const nextTool: PromptLayerMcpTool = {
      type: "mcp",
      mcp_server_id: serverId,
      _resolved_name: serverName,
      _is_available: true,
    };
    if (editingMcp) {
      const index = functions.findIndex(
        candidate => isMcpTool(candidate) && candidate.mcp_server_id === editingMcp.mcp_server_id,
      );
      if (index >= 0) {
        const nextFunctions = [...functions];
        nextFunctions[index] = nextTool;
        setFunctions(nextFunctions);
      }
    } else {
      setFunctions([...functions, nextTool]);
    }
    handleClose();
  };

  return (
    <div className="space-y-5">
      <div>
        <Label className="text-lg font-semibold">
          {editingMcp ? "Change MCP Server" : "Add MCP Server"}
        </Label>
        <p className="mt-1 text-sm text-gray-600">
          Choose a saved workspace connection. Its latest configuration is used on every run.
        </p>
      </div>
      <Input
        value={search}
        onChange={event => setSearch(event.target.value)}
        placeholder="Search MCP servers..."
      />
      <div className="max-h-[360px] space-y-2 overflow-y-auto">
        {servers.data?.map(server => {
          const isAlreadyLinked = functions.some(
            candidate =>
              isMcpTool(candidate) &&
              candidate.mcp_server_id === server.id &&
              candidate.mcp_server_id !== editingMcp?.mcp_server_id,
          );
          return (
            <button
              type="button"
              className="flex w-full items-center gap-3 rounded-lg border p-3 text-left hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={isAlreadyLinked}
              onClick={() => handleSelect(server.id, server.name)}
              key={server.id}
            >
              <Mcp className="h-5 w-5 shrink-0 text-gray-500" />
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium">{server.name}</span>
                <span className="block truncate text-xs text-gray-500">
                  {server.description || server.config.server_url}
                </span>
              </span>
            </button>
          );
        })}
        {!servers.isLoading && (servers.data?.length ?? 0) === 0 && (
          <div className="rounded-lg border border-dashed p-8 text-center">
            <Mcp className="mx-auto mb-2 h-7 w-7 text-gray-400" />
            <p className="text-sm font-medium">No MCP servers found</p>
            <a
              className="mt-2 inline-block text-sm text-blue-600 hover:underline"
              href={`/workspace/${workspaceId}/settings/mcp-servers`}
              target="_blank"
              rel="noreferrer"
            >
              Create an MCP server
            </a>
          </div>
        )}
      </div>
      <div className="flex justify-end pt-2">
        <Button variant="outline" onClick={handleClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default MCPToolDialog;
